import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr, downloadFile } from "@/lib/api";
import { Card, Loader } from "@/components/ui-helpers";
import { EsignPages, ESIGN_COLORS } from "@/components/EsignPages";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Send, Save, Upload, Copy, Download, Ban, X, Search, UserPlus, ChevronDown } from "lucide-react";

function ClientPicker({ clients, value, onChange, onAdded }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [nf, setNf] = useState({ full_name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);
  const selected = clients.find((c) => c.id === value);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const ql = q.trim().toLowerCase();
  const matches = (ql ? clients.filter((c) => (c.full_name || "").toLowerCase().includes(ql) || (c.email || "").toLowerCase().includes(ql)) : clients).slice(0, 8);

  const pick = (c) => { onChange(c.id); setOpen(false); setQ(""); };
  const startAdd = () => { setAdding(true); setNf({ full_name: q.trim(), email: "", phone: "" }); };
  const createClient = async () => {
    if (!nf.full_name.trim()) { toast.error("Enter the client's name"); return; }
    setSaving(true);
    try {
      const r = await api.post("/clients", { full_name: nf.full_name.trim(), email: nf.email.trim(), phone: nf.phone.trim() });
      onAdded?.(r.data); onChange(r.data.id);
      toast.success("Client added");
      setAdding(false); setOpen(false); setQ("");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button type="button" data-testid="esign-client" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-left">
        <span className={selected ? "text-slate-800" : "text-slate-400"}>{selected ? selected.full_name : "Select client *"}</span>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg p-2">
          <div className="flex items-center gap-2 px-2 py-1.5 border border-slate-200 rounded-lg mb-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input autoFocus data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" className="w-full text-sm outline-none" />
          </div>
          <div className="max-h-56 overflow-auto">
            {matches.map((c) => (
              <button key={c.id} data-testid={`client-opt-${c.id}`} onClick={() => pick(c)} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-50 text-sm">
                <div className="font-medium text-slate-800">{c.full_name}</div>
                {c.email && <div className="text-xs text-slate-400">{c.email}</div>}
              </button>
            ))}
            {matches.length === 0 && <div className="px-2 py-2 text-xs text-slate-400">No clients match your search.</div>}
          </div>
          {!adding ? (
            <button data-testid="client-add-new" onClick={startAdd} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-dashed border-slate-300 rounded-lg py-2 text-xs font-semibold text-primary hover:bg-primary/5"><UserPlus className="h-3.5 w-3.5" /> Add new client</button>
          ) : (
            <div className="mt-2 space-y-1.5 border-t border-slate-100 pt-2">
              <input data-testid="newclient-name" value={nf.full_name} onChange={(e) => setNf({ ...nf, full_name: e.target.value })} placeholder="Full name *" className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />
              <input data-testid="newclient-email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} placeholder="Email" className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />
              <input data-testid="newclient-phone" value={nf.phone} onChange={(e) => setNf({ ...nf, phone: e.target.value })} placeholder="Phone" className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />
              <div className="flex gap-2">
                <button onClick={() => setAdding(false)} className="flex-1 border border-slate-200 rounded-lg py-1.5 text-xs font-semibold">Cancel</button>
                <button data-testid="newclient-save" onClick={createClient} disabled={saving} className="flex-1 bg-primary text-white rounded-lg py-1.5 text-xs font-semibold disabled:opacity-60">{saving ? "Adding…" : "Add & select"}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const FIELD_TYPES = [
  { t: "signature", label: "Signature" },
  { t: "initials", label: "Initials" },
  { t: "date", label: "Date" },
  { t: "text", label: "Text" },
];
const DEFAULT_SIZE = { signature: { w: 0.24, h: 0.07 }, initials: { w: 0.12, h: 0.06 }, date: { w: 0.16, h: 0.04 }, text: { w: 0.24, h: 0.04 } };
const uid = () => Math.random().toString(36).slice(2, 10);

function CreateForm() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [f, setF] = useState({ title: "", client_id: "", application_id: "", message: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!f.client_id) { setCases([]); return; }
    api.get("/applications", { params: { client_id: f.client_id } }).then((r) => setCases(r.data)).catch(() => setCases([]));
  }, [f.client_id]);

  const submit = async () => {
    if (!f.title.trim()) return toast.error("Give the document a title");
    if (!file) return toast.error("Upload a PDF");
    if (!f.client_id) return toast.error("Pick the client this document is for");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", f.title);
      fd.append("client_id", f.client_id);
      fd.append("application_id", f.application_id || "");
      fd.append("message", f.message || "");
      const r = await api.post("/esign/envelopes", fd);
      toast.success("Draft created — now place your fields");
      nav(`/admin/esign/${r.data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl">
      <h1 className="font-head text-2xl font-extrabold text-slate-900 mb-6">New signature request</h1>
      <Card className="p-5 space-y-3">
        <input data-testid="esign-title" placeholder="Document title *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
        <ClientPicker clients={clients} value={f.client_id} onChange={(id) => setF({ ...f, client_id: id, application_id: "" })}
          onAdded={(c) => setClients((cs) => [c, ...cs])} />
        <select data-testid="esign-case" value={f.application_id} onChange={(e) => setF({ ...f, application_id: e.target.value })} disabled={!cases.length} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm disabled:opacity-50">
          <option value="">Optional: link a case</option>
          {cases.map((c) => <option key={c.id} value={c.id}>{c.service_type}{c.app_number ? ` · ${c.app_number}` : ""}</option>)}
        </select>
        <textarea data-testid="esign-message" placeholder="Optional message to recipients" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
        <input ref={fileRef} type="file" accept=".pdf" className="hidden" data-testid="esign-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <button onClick={() => fileRef.current?.click()} className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-4 text-sm text-slate-600 hover:border-primary"><Upload className="h-4 w-4" /> {file ? file.name : "Upload PDF"}</button>
        <button data-testid="esign-create-submit" onClick={submit} disabled={saving} className="w-full bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-60">{saving ? "Creating…" : "Create & place fields"}</button>
      </Card>
    </div>
  );
}

export default function EsignBuilder() {
  const { id } = useParams();
  if (!id || id === "new") return <CreateForm />;
  return <Editor id={id} />;
}

function Editor({ id }) {
  const nav = useNavigate();
  const [env, setEnv] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [fields, setFields] = useState([]);
  const [cases, setCases] = useState([]);
  const [selRec, setSelRec] = useState("");
  const [selType, setSelType] = useState("signature");
  const [required, setRequired] = useState(true);
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);

  const load = () => api.get(`/esign/envelopes/${id}`).then((r) => {
    setEnv(r.data); setRecipients(r.data.recipients || []); setFields(r.data.fields || []);
    if (!selRec && (r.data.recipients || []).length) setSelRec(r.data.recipients[0].id);
  }).catch(() => setEnv(false));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (env?.client_id) api.get("/applications", { params: { client_id: env.client_id } }).then((r) => setCases(r.data)).catch(() => {}); }, [env?.client_id]);

  if (env === null) return <Loader />;
  if (env === false) return <div className="p-6 text-slate-500">Request not found.</div>;

  const isDraft = env.status === "Draft";
  const colorFor = (rid) => { const i = recipients.findIndex((r) => r.id === rid); return ESIGN_COLORS[(i < 0 ? 0 : i) % ESIGN_COLORS.length]; };

  const addRecipient = () => setRecipients([...recipients, { id: uid(), name: "", email: "", kind: "signer", order: recipients.length }]);
  const updRecipient = (rid, patch) => setRecipients(recipients.map((r) => r.id === rid ? { ...r, ...patch } : r));
  const delRecipient = (rid) => { setRecipients(recipients.filter((r) => r.id !== rid)); setFields(fields.filter((f) => f.recipient_id !== rid)); };
  const moveRec = (idx, dir) => { const a = [...recipients]; const j = idx + dir; if (j < 0 || j >= a.length) return; [a[idx], a[j]] = [a[j], a[idx]]; setRecipients(a); };

  const placeField = (page, xf, yf) => {
    if (!selRec) return toast.error("Add and select a recipient first");
    const sz = DEFAULT_SIZE[selType];
    setFields([...fields, { id: uid(), recipient_id: selRec, type: selType, page, x: Math.max(0, xf - sz.w / 2), y: Math.max(0, yf - sz.h / 2), w: sz.w, h: sz.h, required }]);
  };
  const placeFromDrop = (page, xf, yf, type) => {
    const t = type || selType;
    if (!selRec) return toast.error("Add and select a recipient first");
    const sz = DEFAULT_SIZE[t] || DEFAULT_SIZE.signature;
    setFields((fs) => [...fs, { id: uid(), recipient_id: selRec, type: t, page, x: Math.min(0.98, Math.max(0, xf - sz.w / 2)), y: Math.min(0.98, Math.max(0, yf - sz.h / 2)), w: sz.w, h: sz.h, required }]);
  };
  const delField = (fid) => setFields((fs) => fs.filter((f) => f.id !== fid));

  const onFieldDown = (e, f) => {
    e.stopPropagation();
    const pageEl = e.currentTarget.closest("[data-page]");
    const rect = pageEl.getBoundingClientRect();
    drag.current = { id: f.id, rect, offx: e.clientX - (rect.left + f.x * rect.width), offy: e.clientY - (rect.top + f.y * rect.height) };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onFieldMove = (e) => {
    if (!drag.current) return;
    const { id: fid, rect, offx, offy } = drag.current;
    const nx = Math.min(0.98, Math.max(0, (e.clientX - offx - rect.left) / rect.width));
    const ny = Math.min(0.98, Math.max(0, (e.clientY - offy - rect.top) / rect.height));
    setFields((fs) => fs.map((f) => f.id === fid ? { ...f, x: nx, y: ny } : f));
  };
  const onFieldUp = () => { drag.current = null; };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/esign/envelopes/${id}`, { recipients, fields, title: env.title, message: env.message, client_id: env.client_id, application_id: env.application_id });
      setEnv(r.data); setRecipients(r.data.recipients); setFields(r.data.fields);
      toast.success("Saved");
      return r.data;
    } catch (e) { toast.error(apiErr(e)); throw e; } finally { setBusy(false); }
  };
  const send = async () => {
    try { await save(); } catch { return; }
    setBusy(true);
    try { await api.post(`/esign/envelopes/${id}/send`); toast.success("Sent to the first recipient"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const copyLink = async () => { try { const r = await api.get(`/esign/envelopes/${id}/link`); await navigator.clipboard.writeText(r.data.url); toast.success("Link copied"); } catch (e) { toast.error(apiErr(e)); } };
  const resend = async () => { try { const r = await api.post(`/esign/envelopes/${id}/resend`); toast.success(`Resent to ${r.data.sent_to}`); } catch (e) { toast.error(apiErr(e)); } };
  const voidEnv = async () => { if (!window.confirm("Void this request?")) return; try { await api.post(`/esign/envelopes/${id}/void`); toast.success("Voided"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const download = () => downloadFile(`/esign/envelopes/${id}/signed`, env.signed_pdf_name || "signed.pdf").catch((e) => toast.error(apiErr(e)));

  const overlay = (pageIdx) => fields.filter((f) => f.page === pageIdx).map((f) => (
    <div key={f.id} data-testid={`field-${f.id}`}
      onPointerDown={isDraft ? (e) => onFieldDown(e, f) : undefined} onPointerMove={isDraft ? onFieldMove : undefined} onPointerUp={isDraft ? onFieldUp : undefined}
      className="absolute rounded text-[10px] font-semibold flex items-center justify-center pointer-events-auto select-none"
      style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`,
        background: `${colorFor(f.recipient_id)}22`, border: `1.5px solid ${colorFor(f.recipient_id)}`, color: colorFor(f.recipient_id), cursor: isDraft ? "move" : "default", touchAction: "none" }}>
      {f.type}{f.required ? "*" : ""}
      {isDraft && (
        <button data-testid={`field-del-${f.id}`} title="Remove field"
          onPointerDown={(e) => { e.stopPropagation(); }} onPointerUp={(e) => { e.stopPropagation(); }}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); delField(f.id); }}
          className="absolute -top-2.5 -right-2.5 bg-white rounded-full border border-slate-300 shadow w-5 h-5 flex items-center justify-center text-rose-600 hover:bg-rose-50 z-10">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  ));

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div><button onClick={() => nav("/admin/esign")} className="text-xs text-slate-500 mb-1">← All requests</button>
          <h1 className="font-head text-xl font-extrabold text-slate-900">{env.title}</h1>
          <span className="text-xs text-slate-500">Status: {env.status}{env.with_whom ? ` · with ${env.with_whom}` : ""}</span></div>
        <div className="flex items-center gap-2">
          {!isDraft && env.status === "Sent" && <button data-testid="esign-copylink" onClick={copyLink} className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-2 rounded-lg text-xs font-semibold"><Copy className="h-3.5 w-3.5" /> Copy link</button>}
          {!isDraft && env.status === "Sent" && <button onClick={resend} className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-2 rounded-lg text-xs font-semibold"><Send className="h-3.5 w-3.5" /> Resend</button>}
          {env.status === "Completed" && <button data-testid="esign-download" onClick={download} className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-2 rounded-lg text-xs font-semibold"><Download className="h-3.5 w-3.5" /> Signed PDF</button>}
          {(env.status === "Sent" || env.status === "Draft") && <button onClick={voidEnv} className="inline-flex items-center gap-1.5 border border-rose-200 text-rose-600 px-3 py-2 rounded-lg text-xs font-semibold"><Ban className="h-3.5 w-3.5" /> Void</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <Card className="p-4 overflow-auto bg-slate-50">
          <EsignPages pdfUrl={`/esign/envelopes/${id}/pdf`} onPageClick={isDraft ? placeField : undefined} onPageDrop={isDraft ? placeFromDrop : undefined} renderOverlay={(idx) => overlay(idx)} />
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <h3 className="font-head font-bold text-slate-900 mb-2">Recipients</h3>
            <div className="space-y-2">
              {recipients.map((r, i) => (
                <div key={r.id} data-testid={`recipient-${i}`} className={`rounded-xl border p-2.5 ${selRec === r.id ? "border-primary ring-1 ring-primary/30" : "border-slate-200"}`} onClick={() => setSelRec(r.id)}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="h-3 w-3 rounded-full shrink-0" style={{ background: ESIGN_COLORS[i % ESIGN_COLORS.length] }} />
                    <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
                    {isDraft && <div className="ml-auto flex items-center gap-1">
                      <button onClick={(e) => { e.stopPropagation(); moveRec(i, -1); }} className="text-slate-400"><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); moveRec(i, 1); }} className="text-slate-400"><ArrowDown className="h-3.5 w-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); delRecipient(r.id); }} className="text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>}
                  </div>
                  <input data-testid={`recipient-name-${i}`} disabled={!isDraft} placeholder="Name" value={r.name} onChange={(e) => updRecipient(r.id, { name: e.target.value })} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm mb-1" />
                  {r.kind !== "rcic" && <input data-testid={`recipient-email-${i}`} disabled={!isDraft} placeholder="Email" value={r.email} onChange={(e) => updRecipient(r.id, { email: e.target.value })} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />}
                  <label className="flex items-center gap-2 text-xs text-slate-600 mt-1"><input type="checkbox" disabled={!isDraft} checked={r.kind === "rcic"} onChange={(e) => updRecipient(r.id, { kind: e.target.checked ? "rcic" : "signer" })} /> RCIC auto-sign (no email)</label>
                </div>
              ))}
            </div>
            {isDraft && <button data-testid="add-recipient" onClick={addRecipient} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-slate-200 rounded-lg py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Plus className="h-3.5 w-3.5" /> Add recipient</button>}
          </Card>

          {isDraft && (
            <Card className="p-4">
              <h3 className="font-head font-bold text-slate-900 mb-2">Fields</h3>
              <p className="text-[11px] text-slate-500 mb-2">Select a recipient, then <b>drag a field onto the document</b> (or click a field then click the page). Drag a placed field to move it; click its × to remove.</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {FIELD_TYPES.map((ft) => (
                  <button key={ft.t} data-testid={`fieldtype-${ft.t}`} draggable
                    onDragStart={(e) => { setSelType(ft.t); e.dataTransfer.setData("text/plain", ft.t); e.dataTransfer.effectAllowed = "copy"; }}
                    onClick={() => setSelType(ft.t)}
                    className={`px-2 py-2 rounded-lg text-xs font-semibold border cursor-grab active:cursor-grabbing ${selType === ft.t ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600"}`}>{ft.label}</button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> Required field</label>
            </Card>
          )}

          {isDraft && (
            <div className="flex gap-2">
              <button data-testid="esign-save" onClick={save} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-slate-200 px-4 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-60"><Save className="h-4 w-4" /> Save</button>
              <button data-testid="esign-send" onClick={send} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-primary text-white px-4 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-60"><Send className="h-4 w-4" /> Send</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
